import ReactDOM from "react-dom/client";
import { BrowserRouter, useNavigate } from "react-router-dom";
import { StyleSheetManager } from "styled-components";
import isPropValid from "@emotion/is-prop-valid";
import {
  AuthUserProvider,
  BrandProvider,
  createDefaultBrandsConfig,
  getAuthConfig,
  getAuthConfigByKey,
  InvestIndustrialIcon,
  InvestIndustrialIconDark,
  InvestIndustrialLogo,
  InvestIndustrialLogoDark,
  investIndustrialIconStacked,
  investIndustrialIconStackedDark,
  NGVIcon,
  PathwayFullLogo,
  PathwayLogo,
  registerBrandIcons,
  SessionTimeoutProvider,
  useAuth,
  useUser,
  VITE_MODE_MAPPING,
  WeBuildAiIcon,
  WeBuildAiIconStacked,
  type LogoutReason,
} from "@webuild-ai/react-auth";
import {
  clearAppSessionData,
  clearMsalCache,
  logLogoutEvent,
} from "@webuild-ai/react-auth/services";
import React, { useCallback } from "react";

import App from "./App.tsx";
import i18n, { REACT_AUTH_NAMESPACE } from "./i18n";
// Load Iconify configuration to prevent CDN/CORS issues
import "./iconify-config";
import { HeroProvider } from "./providers/HeroProvider.tsx";
import "@/styles/globals.css";
import { WeBuildClientProvider } from "./providers/WeBuildClientProvider.tsx";
import { RBACProviderWrapper } from "./providers/RBACProviderWrapper.tsx";
import { EmbeddedAuthProvider, useEmbeddedAuthConfig } from "./providers/EmbeddedAuthProvider.tsx";
import { useIdpSessionHandler } from "./hooks/useIdpSessionHandler.ts";
import { clearWorkflowListCache } from "./hooks/chat/useWorkflowList.ts";
import { ThemeProvider } from "./hooks/use-theme.tsx";
import ErrorBoundary from "./services/ErrorBoundaries.tsx";
import { TelemetryProvider, logger } from "./telemetry";
import { siteConfig } from "./config/site";

// TODO(NGET): awaiting react-auth release of NGETIcon — alias NGVIcon as a placeholder so the
// file type-checks. Swap this for `import { NGETIcon } from "@webuild-ai/react-auth"` once the
// package exports it. The `typeof NGVIcon` annotation anchors the structural type so any
// future signature drift on the real export breaks the build instead of being silently masked.
const NGETIcon: typeof NGVIcon = NGVIcon;

// HeroUI's toast queue wraps every update in `document.startViewTransition()` and
// never attaches a `.catch()` to the returned `transition.finished` promise. When two
// toasts are added in quick succession (e.g. "Exporting…" → "Exported successfully" on a
// fast Excel/PDF/Word export) the second transition is aborted and the rejection bubbles
// up as an unhandled `DOMException: Skipped ViewTransition…`. The DOM still updates via
// the synchronous fallback, so this is purely console noise — swallow only that exact
// rejection and let everything else propagate.
// Chromium emits several distinct DOMException messages when a view transition aborts:
//   - "Skipped ViewTransition due to another transition starting" (older)
//   - "Transition was skipped"                                    (newer)
//   - "Transition was aborted because of invalid state"           (mid-flight nav / DOM swap)
// All are benign in the context of HeroUI's toast queue (which wraps every update in
// `document.startViewTransition()` without `.catch()`-ing the rejection). Match the family.
const VIEW_TRANSITION_NOISE_RE = /ViewTransition|Transition was (skipped|aborted)/i;

window.addEventListener("unhandledrejection", (event) => {
  const reason = event.reason as unknown;

  if (reason instanceof DOMException && VIEW_TRANSITION_NOISE_RE.test(reason.message)) {
    event.preventDefault();
  }
});

// Register brand icons BEFORE rendering - required for BrandProvider
registerBrandIcons({
  InvestIndustrialLogo,
  InvestIndustrialLogoDark,
  WeBuildAiIcon,
  NGVIcon,
  NGETIcon,
  investIndustrialIconStacked,
  investIndustrialIconStackedDark,
  WeBuildAiIconStacked,
  PathwayLogo,
  PathwayFullLogo,
  InvestIndustrialIcon,
  InvestIndustrialIconDark,
});

// Get auth config at module level for BrandProvider initialBrand
// First try explicit mode mapping, then fall back to hostname-based detection
const mode = import.meta.env.MODE as string;
const configKey = VITE_MODE_MAPPING[mode];
const initialAuthConfig = configKey ? getAuthConfigByKey(configKey) : getAuthConfig();
const initialBrand = initialAuthConfig?.brand;

// Build custom brands config with env var override for NGV dashboard URL
// IMPORTANT: Pass the registered icons to createDefaultBrandsConfig to get proper icons
// (without icons, it returns PlaceholderIcon for all brands)
const ngvDashboardUrl =
  (import.meta.env.VITE_NGV_DASHBOARD_URL as string | undefined) ||
  (initialBrand === "ngv" ? `${siteConfig().platformAPIURL}/ncrmweb` : undefined);
// TODO(NGET): NGET does not have its own dashboard yet; strip the inherited NGV dashboard
// config below so the sidebar entry and route are not exposed for NGET tenants.
const registeredIcons = {
  InvestIndustrialLogo,
  InvestIndustrialLogoDark,
  WeBuildAiIcon,
  NGVIcon,
  NGETIcon,
  investIndustrialIconStacked,
  investIndustrialIconStackedDark,
  WeBuildAiIconStacked,
  PathwayLogo,
  PathwayFullLogo,
  InvestIndustrialIcon,
  InvestIndustrialIconDark,
};
const defaultBrands = createDefaultBrandsConfig(registeredIcons);
const defaultNgvBrand = defaultBrands.ngv;
// TODO(NGET): drop this NGV fallback once react-auth ships defaultBrands.nget with NGET's
// own palette. Until then we alias to NGV so colours, icons and fonts match National Grid
// in dev.
const defaultNgvAsNget = defaultBrands.nget ?? defaultBrands.ngv;
// Strip NGV's dashboard config from the NGET fallback — NGET does not get a dashboard tile
// or the /ncrm sidebar entry. Will need revisiting when react-auth ships a real NGET brand.
const { dashboard: _ngetDashboardOmit, ...defaultNgetBrand } = defaultNgvAsNget;
const customBrands = {
  ngv: {
    ...defaultNgvBrand,
    fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
    ...(ngvDashboardUrl
      ? {
          dashboard: {
            type: "iframe" as const,
            url: ngvDashboardUrl,
            title: defaultNgvBrand.dashboard?.title,
            subtitle: defaultNgvBrand.dashboard?.subtitle,
            showInSideBar: defaultNgvBrand.dashboard?.showInSideBar,
            urlSegment: defaultNgvBrand.dashboard?.urlSegment,
          },
        }
      : {}),
  },
  nget: {
    ...defaultNgetBrand,
    fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
  },
};

// Wrapper component to provide navigation to AuthUserProvider
const AuthProviderWithNavigation: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const mode = import.meta.env.MODE as string;

  // Embedded (iframe) SSO config from the parent-window handshake. Inert when standalone.
  const { isEmbedded, loginHint, externalToken, onInteractionRequired, onAuthEvent } =
    useEmbeddedAuthConfig();

  // Get auth config using explicit mode mapping, fall back to hostname-based detection
  // This allows a single build to work across multiple deployment targets
  const configKey = VITE_MODE_MAPPING[mode];
  const authConfig = configKey ? getAuthConfigByKey(configKey) : getAuthConfig();

  // Simple fallback if no config found (should not happen with hostname detection)
  if (!authConfig) {
    logger.error(`No auth config found for mode: ${mode}`, undefined, {
      mode,
      configKey: configKey ?? "undefined",
    });

    return <div>Configuration error for mode: {mode}</div>;
  }

  return (
    <AuthUserProvider
      authConfig={authConfig}
      embedded={isEmbedded}
      externalToken={externalToken}
      loginHint={loginHint}
      onAuthEvent={onAuthEvent}
      onInteractionRequired={onInteractionRequired}
      onRedirectCallback={(path) => navigate(path)}
    >
      {children}
    </AuthUserProvider>
  );
};

/**
 * Session Timeout Wrapper - Wraps SessionTimeoutProvider with required callbacks.
 *
 * This component:
 * - Provides the logout callback for session expiry
 * - Handles IdP session expiration events from WeBuildClientProvider
 * - Performs hard logout when session expires
 *
 * NOTE: SessionTimeoutProvider (from react-auth v2.1.7+) automatically detects auth state
 * and prevents MSAL `interaction_in_progress` errors. The `useIdpSessionHandler` hook
 * still requires manual auth guards since it's Pathway-specific.
 */
const SessionTimeoutWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { logout, isLoading, isAuthenticated } = useAuth();
  const { isEmbedded } = useEmbeddedAuthConfig();
  const { userProfile } = useUser();
  const authConfig = getAuthConfig();

  // Use i18n.t directly to get translations from the reactAuth namespace
  // This avoids useTranslation hook which can cause issues during auth redirect flows
  const t = useCallback((key: string) => i18n.t(key, { ns: REACT_AUTH_NAMESPACE }), []);

  // Guard: Don't enable session timeout management until auth is fully initialised
  // This prevents the MSAL `interaction_in_progress` error during redirect flows
  const sessionEnabled = !isLoading && isAuthenticated;

  // Handle IdP session expiration events from WeBuildClientProvider
  // Only set up the handler when authenticated to avoid race conditions
  useIdpSessionHandler({ enabled: sessionEnabled });

  // Get tenant from user profile
  const tenant = userProfile?.tenant || "default";

  /**
   * Handle session expiry by performing hard logout.
   */
  const handleSessionExpired = useCallback(
    async (reason: "absolute" | "idle" | "idp_revoked" | "manual") => {
      const logoutReason: LogoutReason =
        reason === "absolute"
          ? "session_expired"
          : reason === "idle"
            ? "idle_timeout"
            : reason === "manual"
              ? "user_requested"
              : "idp_revoked";

      logLogoutEvent(logoutReason, { provider: authConfig.provider });

      // Clear app session data first
      clearAppSessionData();
      // Drop the in-memory workflow list cache so it cannot leak into the next session.
      clearWorkflowListCache();

      // In embedded mode there is no local MSAL session to clear; the parent owns sign-out.
      if (authConfig.provider === "Azure" && !isEmbedded) {
        // Clear MSAL cache for Azure
        clearMsalCache();
      }

      // Use the abstracted logout from useAuth which handles both Auth0 and Azure.
      // In embedded mode this emits embedded_logout (no redirect) and posts REQUEST_LOGOUT.
      await logout();
    },
    [authConfig.provider, logout, isEmbedded],
  );

  return (
    <SessionTimeoutProvider t={t} tenantKey={tenant} onSessionExpired={handleSessionExpired}>
      {children}
    </SessionTimeoutProvider>
  );
};

ReactDOM.createRoot(document.getElementById("root")!).render(
  // <React.StrictMode>
  <ErrorBoundary>
    <BrandProvider customBrands={customBrands} initialBrand={initialBrand}>
      <ThemeProvider>
        <BrowserRouter basename={import.meta.env.BASE_URL}>
          <HeroProvider>
            <EmbeddedAuthProvider>
              <AuthProviderWithNavigation>
                <TelemetryProvider>
                  <WeBuildClientProvider>
                    <RBACProviderWrapper>
                      <SessionTimeoutWrapper>
                        <StyleSheetManager
                          //required for styled-components to work
                          shouldForwardProp={(prop, _) => isPropValid(prop)}
                        >
                          <App />
                        </StyleSheetManager>
                      </SessionTimeoutWrapper>
                    </RBACProviderWrapper>
                  </WeBuildClientProvider>
                </TelemetryProvider>
              </AuthProviderWithNavigation>
            </EmbeddedAuthProvider>
          </HeroProvider>
        </BrowserRouter>
      </ThemeProvider>
    </BrandProvider>
  </ErrorBoundary>,
  // </React.StrictMode>,
);
